import { localizationService } from "../localization";
import { Color, Quaternion, Vector3 } from "flavr-math";
import { TextHorizontalAlign, TextVerticalAlign } from "flavr-common";
import { FacetRenderer } from "../rendering/renderable/Renderable";
import { Text } from "troika-three-text";
export class AnnotationRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.data = [];
    }
    destroy() {
        for (const it of this.data) {
            it.textMesh.geometry.dispose();
            if (!Array.isArray(it.textMesh.material)) {
                it.textMesh.material.dispose();
            }
            else {
                for (const material of it.textMesh.material) {
                    material.dispose();
                }
            }
            it.lineInstance.destroy();
        }
        this.data = [];
    }
    setVisible(visible) {
        for (const it of this.data) {
            it.textMesh.visible = visible;
            it.lineInstance.setVisible(visible);
        }
    }
    updateAnnotation(index, data) {
        const annotation = this.data[index];
        if (!annotation || !annotation.lineInstance || !annotation.textMesh)
            return;
        const color = new Color().setHex(data.lineColor || 0);
        annotation.lineInstance.setColor(color);
        data.lineWidth && (annotation.lineWidth = data.lineWidth);
        let hAlign = TextHorizontalAlign.Center;
        let vAlign = TextVerticalAlign.Center;
        annotation.position.fromArray(data.position);
        annotation.anchorPoint.fromArray(data.anchorPoint);
        const dir = annotation.position.clone().sub(annotation.anchorPoint);
        if (Math.abs(dir.x) > Math.abs(dir.y)) {
            hAlign = dir.x > 0 ? TextHorizontalAlign.Left : TextHorizontalAlign.Right;
        }
        else {
            vAlign = dir.y > 0 ? TextVerticalAlign.Bottom : TextVerticalAlign.Top;
        }
        annotation.textMesh.text = localizationService.getTranslation(data.text);
        switch (hAlign) {
            case TextHorizontalAlign.Left:
                annotation.textMesh.textAlign = "left";
                break;
            case TextHorizontalAlign.Right:
                annotation.textMesh.textAlign = "right";
                break;
            default:
                annotation.textMesh.textAlign = "center";
        }
        switch (vAlign) {
            case TextVerticalAlign.Top:
                annotation.textMesh.anchorY = "top";
                break;
            case TextVerticalAlign.Bottom:
                annotation.textMesh.anchorY = "bottom";
                break;
            default:
                annotation.textMesh.anchorY = "middle";
        }
        annotation.textMesh.gpuAccelerateSDF = true;
        annotation.textMesh.rotateY(Math.PI);
        annotation.textMesh.sync();
    }
    updateFacet(facet) {
        for (let i = 0; i < facet.annotations.length; ++i) {
            const annotation = facet.annotations[i];
            if (i >= this.data.length) {
                const lineInstance = this.owner.renderer.createInstancedLine(this.owner.modelUID);
                lineInstance.setFuzzyness(0);
                lineInstance.setNoiseFrequency(0);
                lineInstance.setNoiseVelocity(0);
                this.data.push({
                    lineInstance,
                    textMesh: new Text(),
                    lineWidth: 0.1,
                    position: new Vector3(),
                    anchorPoint: new Vector3()
                });
            }
            this.updateAnnotation(i, annotation);
        }
        while (this.data.length > facet.annotations.length) {
            const it = this.data.pop();
            it && it.lineInstance.destroy();
            it && it.textMesh.geometry.dispose();
            if (it && !Array.isArray(it.textMesh.material)) {
                it.textMesh.material.dispose();
            }
            else if (it && Array.isArray(it.textMesh.material)) {
                for (const material of it.textMesh.material) {
                    material.dispose();
                }
            }
        }
    }
    updateTransform() {
        const position = new Vector3();
        const quaternion = new Quaternion();
        const scale = new Vector3();
        this.owner.getWorldTransform(position, quaternion, scale);
        const averageWorldScale = (scale.x + scale.y + scale.z) / 3;
        for (const it of this.data) {
            const pos = new Vector3().copy(it.position).applyMatrix4(this.owner.matrixWorld);
            if (it.textMesh) {
                it.textMesh.position.copy(pos);
                it.textMesh.quaternion.copy(quaternion);
                it.textMesh.scale.copy(scale);
                it.textMesh.rotateY(Math.PI);
                it.textMesh.sync();
            }
            if (it.lineInstance) {
                const thickness = it.lineWidth * averageWorldScale;
                it.lineInstance.setStartPoint(pos);
                it.lineInstance.setEndPoint(new Vector3().copy(it.anchorPoint).applyMatrix4(this.owner.matrixWorld));
                it.lineInstance.setThickness(thickness);
            }
        }
    }
}
